import { Combobox } from '@headlessui/react'
import { FC, HTMLAttributes, Fragment, useState, ReactElement } from 'react'
import { Wrapper as GoogleWrapper, Status } from '@googlemaps/react-wrapper'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

interface Props extends HTMLAttributes<HTMLInputElement> {
  onSelection: (selected: string) => void
  initialValue: string
}

const LocationAutocomplete: FC<Props> = ({
  onSelection,
  initialValue,
  ...rest
}) => {
  /**
   * Handle internal state of the combo box
   */
  const [value, setValue] = useState(initialValue)

  const predictionOpts = value => ({
    input: value,
    types: ['address'],
    componentRestrictions: { country: 'USA' }
  })

  const { placePredictions, getPlacePredictions } = usePlacesService({
    options: predictionOpts(value)
  })

  const handleInputChange = evt => {
    if (!evt.target.value) {
      return setValue('')
    }

    setValue(evt.target.value)

    getPlacePredictions(predictionOpts(evt.target.value))

    // user might not select a suggestion, we emit the value to parent as a backup
    onSelection(evt.target.value.trim())
  }

  const handleSelection = (
    place: google.maps.places.AutocompletePrediction & any
  ) => {
    setValue(place.description.replace(', USA', ''))
    onSelection(place.description)
  }

  return (
    <GoogleWrapper
      apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
      libraries={['places']}
      render={render}
    >
      <Combobox value={value} onChange={handleSelection}>
        <div className="pos-relative mt-1">
          <div className="pos-relative w-full text-left">
            <Combobox.Input
              {...rest}
              autoComplete="off"
              onChange={handleInputChange}
            />
          </div>
          {!!placePredictions?.length && !!value?.length && (
            <Combobox.Options
              style={{ minHeight: '6rem' }}
              className="List pos-absolute w-full py-1 mt-1 mx-0 mb-0 type-body-one bg-white rounded-md box-shadow-1 max-h-96 z-layer-one"
            >
              {placePredictions.map((item, i) => (
                <Combobox.Option key={i} value={item} as={Fragment}>
                  {({ active }) => (
                    <li
                      className={`${
                        active ? 'bg-gray-light' : 'bg-white'
                      } px-3 py-2 cursor-pointer`}
                    >
                      {item.description.replace(', USA', '')}
                    </li>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
    </GoogleWrapper>
  )
}

export default LocationAutocomplete

const render = (status: Status): ReactElement => {
  if (status === Status.FAILURE) return null
  // Can put a loading spinner here
  if (status === Status.LOADING) return null
}
